import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {AuthenticateUser} from '../../../../../Utils/_helpers/RoleAndPermission'
import moment from 'moment-timezone'
import {toast} from 'react-toastify'
import {List} from '../../../../../../services/cellcast.v2.types'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import './ListMobileCard.scss'

interface ListMobileAction {
  key: string
  label: string
  icon?: string
  danger?: boolean
  onClick: () => void
}

interface ListMobileCardProps {
  list: List & {contactCount?: number; updatedAt?: string}
  isDefaultRegion?: boolean
  contactCount?: number | string
  isCountLoading?: boolean
  canSendQm?: boolean
  onEdit: (list: List) => void
  onDelete: (list: List) => void
  onAddContact: (list: List) => void
  onSendBulkMessage: (listId: string) => void
  onSendQuickMessage: (list: List) => void
  onCopyToList: (listId: string) => void
  onMoveToList: (listId: string) => void
  onManageContacts: (list: List) => void
}

export const ListMobileCard: React.FC<ListMobileCardProps> = ({
  list,
  isDefaultRegion = false,
  contactCount,
  isCountLoading = false,
  canSendQm = false,
  onEdit,
  onDelete,
  onAddContact,
  onSendBulkMessage,
  onSendQuickMessage,
  onCopyToList,
  onMoveToList,
  onManageContacts,
}) => {
  const [showActions, setShowActions] = useState(false)
  const navigate = useNavigate()

  const handleActionClick = () => {
    setShowActions(!showActions)
  }

  const actions: ListMobileAction[] = []

  if (AuthenticateUser('upload-contacts')) {
    actions.push({
      key: 'add-contacts',
      label: 'Add Contacts',
      icon: 'bi bi-person-lines-fill',
      onClick: () => {
        onAddContact(list)
        setShowActions(false)
      },
    })
  }

  if (AuthenticateUser('send-quick-sms')) {
    actions.push({
      key: 'send-bulk-message',
      label: 'Send Bulk Message',
      icon: 'bi bi-send-fill',
      onClick: () => {
        onSendBulkMessage(list._id)
        setShowActions(false)
      },
    })
  }

  if (AuthenticateUser('send-quick-sms') && canSendQm) {
    actions.push({
      key: 'send-quick-message',
      label: 'Send Quick Message',
      icon: 'bi bi-send',
      onClick: () => {
        onSendQuickMessage(list)
        setShowActions(false)
      },
    })
  }

  if (AuthenticateUser('edit-contact')) {
    actions.push({
      key: 'edit',
      label: 'Edit List Name',
      icon: 'bi bi-pencil',
      onClick: () => {
        onEdit(list)
        setShowActions(false)
      },
    })
  }

  if (AuthenticateUser('edit-contact') && isDefaultRegion) {
    actions.push({
      key: 'copy-to-list',
      label: 'Copy to another List',
      icon: 'bi bi-list',
      onClick: () => {
        onCopyToList(list._id)
        setShowActions(false)
      },
    })
  }

  if (AuthenticateUser('edit-contact') && isDefaultRegion) {
    actions.push({
      key: 'move-to-list',
      label: 'Move to another List',
      icon: 'bi bi-scissors',
      onClick: () => {
        onMoveToList(list._id)
        setShowActions(false)
      },
    })
  }

  if (AuthenticateUser('edit-contact')) {
    actions.push({
      key: 'manage-contacts',
      label: 'Manage Contact',
      icon: 'bi bi-person-lines-fill',
      onClick: () => {
        onManageContacts(list)
        setShowActions(false)
      },
    })
  }

  actions.push({
    key: 'copy-id',
    label: 'Copy ID',
    icon: 'bi bi-clipboard',
    onClick: () => {
      navigator.clipboard.writeText(list._id)
      toast.success('Successfully copied list id')
      setShowActions(false)
    },
  })

  if (AuthenticateUser('edit-contact')) {
    actions.push({
      key: 'delete',
      label: 'Delete',
      icon: 'bi bi-trash',
      danger: true,
      onClick: () => {
        onDelete(list)
        setShowActions(false)
      },
    })
  }

  return (
    <div className='list-mobile-card'>
      {/* Header with list name and actions */}
      <div className='card-header-section'>
        <div className='list-info-section'>
          <h5
            className='list-name cursor-pointer'
            onClick={() => navigate('/contacts/list/' + list._id)}
          >
            {list?.name}
          </h5>
        </div>
        <div className='d-flex gap-1 align-items-center'>
          {AuthenticateUser('upload-contacts') && (
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id='button-tooltip'>Add contacts</Tooltip>}
            >
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light-primary'
                onClick={() => onAddContact(list)}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
              </button>
            </OverlayTrigger>
          )}
          <button
            className='btn btn-action-mobile'
            onClick={handleActionClick}
            aria-label='Actions menu'
            aria-expanded={showActions}
          >
            <i className='bi bi-three-dots-vertical'></i>
          </button>
        </div>
      </div>

      {/* Actions dropdown */}
      {showActions && (
        <div className='actions-dropdown'>
          {actions.map((action) => (
            <button
              key={action.key}
              className={`action-item ${action.danger ? 'text-danger' : ''}`}
              onClick={action.onClick}
            >
              {action.icon && <i className={`${action.icon} me-2`}></i>}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* List details grid */}
      <div className='card-details-grid'>
        {/* Date Created */}
        {list?.createdAt && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-calendar-plus me-1'></i>
              Created Date
            </span>
            <span className='detail-value'>{moment(list.createdAt).format('DD/MM/YYYY')}</span>
          </div>
        )}

        {/* Time Created */}
        {list?.createdAt && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-clock me-1'></i>
              Created Time
            </span>
            <span className='detail-value'>{moment(list.createdAt).format('LT')}</span>
          </div>
        )}

        {/* Date Updated */}
        {list?.updatedAt && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-calendar-check me-1'></i>
              Updated Date
            </span>
            <span className='detail-value'>{moment(list.updatedAt).format('DD/MM/YYYY')}</span>
          </div>
        )}

        {/* Time Updated */}
        {list?.updatedAt && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-clock me-1'></i>
              Updated Time
            </span>
            <span className='detail-value'>{moment(list.updatedAt).format('LT')}</span>
          </div>
        )}

        {/* Contacts Count */}
        <div className='detail-item full-width'>
          <span className='detail-label'>
            <i className='bi bi-people me-1'></i>
            Contacts
          </span>
          <span className='detail-value'>
            {isCountLoading
              ? 'counting....'
              : contactCount !== undefined && contactCount !== null
              ? contactCount
              : 0}
          </span>

          <span className='detail-label'>
            <i className='bi bi-calendar-check me-1'></i>
             Created Date
          </span>
          <span className='detail-value'>{moment(list.createdAt).format('DD/MM/YYYY')}</span>
        </div>

        <div className='detail-item'>
          <span className='detail-label'>
            <i className='bi bi-calendar-check me-1'></i>
            Updated Date
          </span>
          <span className='detail-value'>{moment(list.updatedAt).format('DD/MM/YYYY')}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className='card-actions'>
        {AuthenticateUser('upload-contacts') && (
          <button className='btn btn-light-primary btn-sm' onClick={() => onAddContact(list)}>
            <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
            Add
          </button>
        )}
        {AuthenticateUser('edit-contact') && (
          <button className='btn btn-light btn-sm' onClick={() => onEdit(list)}>
            <i className='bi bi-pencil me-2'></i>
            Edit
          </button>
        )}
        <button
          className='btn btn-light btn-sm'
          onClick={() => navigate('/contacts/list/' + list._id)}
        >
          <i className='bi bi-eye me-2'></i>
          View
        </button>
      </div>
    </div>
  )
}

